import React, { useEffect, useRef, useMemo,useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  Menu,
  MenuItem,
} from "@mui/material";
import AOPDetailsPage from "./AOPDetailsPage";
import POPDetailsPage from "./POPDetailsPage";
import BaseBrokerageDetailsPage from "./BaseBrokerageDetailsPage";
import { useDispatch, useSelector } from "react-redux";
import action from "../../Redux Store/actions";
import { fetchAOPData, fetchAllAOPPlans } from "../../Services/aopService";
import { fetchPOPData } from "../../Services/popService";
import { supabase } from "../../supabaseClient";

// ─── Animated Single Progress Bar ────────────────────────────────────────────
const ProgressBar = ({ value = 0, color = "#6C63FF", height = 8 }) => {
  const [width, setWidth] = React.useState(0);
  const safeValue = value > 0 ? Math.min(Math.max(value, 2), 100) : 0;

  useEffect(() => {
    const t = setTimeout(() => setWidth(safeValue), 250);
    return () => clearTimeout(t);
  }, [safeValue]);

  const tooltipLeft = Math.min(width - 2, 90);

  return (
    <Box sx={{ position: "relative", height, mt: 1.5, mb: 0.5 }}>
      {/* track */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: 5,
          backgroundColor: "#EAEAF0",
          overflow: "hidden",
        }}
      >
        {/* fill */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${width}%`,
            backgroundColor: color,
            borderRadius: 5,
            transition: "width 1.4s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
      </Box>

      {/* floating label on bar */}
      {width > 5 && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: `${tooltipLeft}%`,
            transform: "translateY(-50%)",
            backgroundColor: color,
            color: "#fff",
            fontSize: 9,
            fontWeight: 700,
            px: 0.7,
            py: 0.15,
            borderRadius: 0.8,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            lineHeight: 1.4,
            transition: "left 1.4s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {value.toFixed(0)}%
        </Box>
      )}
    </Box>
  );
};

// ─── Animated Dual Progress Bar (Received + Pending) ─────────────────────────
const DualProgressBar = ({ receivedAmt, pendingAmt, receivedPct, pendingPct }) => {
  const [rW, setRW] = React.useState(0);
  const [pW, setPW] = React.useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setPW(Math.min(receivedPct + pendingPct, 100));
      setRW(Math.min(receivedPct, 100));
    }, 250);
    return () => clearTimeout(t);
  }, [receivedPct, pendingPct]);

  return (
    <Box sx={{ mt: 1, mb: 0 }}>
      {/* Legend row */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
        <Box display="flex" alignItems="center" gap={0.6}>
          <Box
            sx={{
              width: 16, height: 16, borderRadius: "50%",
              border: "2px solid #3BB273",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#3BB273" }} />
          </Box>
          <Typography fontSize={12} color="#3BB273" fontWeight={500}>Received</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.6}>
          <Typography fontSize={12} color="#F9A825" fontWeight={500}>Pending</Typography>
          <Box
            sx={{
              width: 16, height: 16, borderRadius: "50%",
              border: "2px solid #F9A825",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#F9A825" }} />
          </Box>
        </Box>
      </Box>

      {/* Bar */}
      <Box
        sx={{
          position: "relative",
          height: 22,
          borderRadius: "6px",
          backgroundColor: "#EAEAF0",
          overflow: "hidden",
        }}
      >
        {/* pending (amber) — full width behind */}
        <Box
          sx={{
            position: "absolute",
            left: 0, top: 0,
            height: "100%",
            width: `${pW}%`,
            backgroundColor: "#F9C74F",
            transition: "width 1.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
        {/* received (green) — on top */}
        <Box
          sx={{
            position: "absolute",
            left: 0, top: 0,
            height: "100%",
            width: `${rW}%`,
            backgroundColor: "#3BB273",
            borderRadius: "6px 0 0 6px",
            transition: "width 1.3s cubic-bezier(0.4,0,0.2,1) 0.05s",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            overflow: "hidden",
            pr: 0.8,
          }}
        >
          {rW > 12 && (
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>
              ₹{pendingAmt}
            </Typography>
          )}
        </Box>
        {/* pending label in yellow zone */}
        {pW > rW + 10 && (
          <Box
            sx={{
              position: "absolute",
              top: 0, bottom: 0,
              left: `${rW}%`,
              width: `${pW - rW}%`,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              overflow: "hidden",
              pr: 1,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>
              ₹{receivedAmt}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Data transforms ──────────────────────────────────────────────────────────
const transformPOPData = (rows = [], slabs = []) => {
  let totalUnits = 0;
  let totalReward = 0;
  rows.forEach((row) => {
    totalUnits += 1;
    totalReward += Number(row.pop_amount || 0);
  });
  const lastSlab = slabs[slabs.length - 1];
  const target = lastSlab ? Number(lastSlab.from_value || 0) : 0;
  const achievement = target ? Math.min((totalUnits / target) * 100, 100) : 0;
  return { totalUnits, totalReward, target, achievement };
};

const transformAOPData = (rows = [], slabs = []) => {
  let totalValue = 0;
  let totalIncentive = 0;
  rows.forEach((row) => {
    totalValue += Number(row.booking_value || 0);
    totalIncentive += Number(row.aop_amount || 0);
  });
  const lastSlab = slabs[slabs.length - 1];
  const target = lastSlab ? Number(lastSlab.from_value || 0) : 0;
  const achievement = target ? Math.min((totalValue / target) * 100, 100) : 0;
  return { totalValue, totalIncentive, achievement, target };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatCurrency = (num) => Number(num || 0).toLocaleString("en-IN");

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(Number(dateStr));
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getSlabLabel = (slab) => {
  if (slab?.percentage !== null && slab?.percentage !== undefined)
    return `${Number(slab.percentage)}%`;
  if (slab?.amount !== null && slab?.amount !== undefined)
    return `₹${Number(slab.amount).toLocaleString("en-IN")}`;
  return "-";
};

const getBaseBrokerageLabel = (plan) => {
  const value = Number(plan?.value || 0);
  if (plan?.calc_type === "Percentage") return `${value}%`;
  if (plan?.calc_type === "Flat") return `₹${value.toLocaleString("en-IN")}`;
  return `${value}`;
};

// Normalize any FY string to short "FY YY-YY" format
const normalizeFY = (fy) => {
  if (!fy) return null;
  const match = fy.match(/(\d{2,4})-(\d{2,4})/);
  if (!match) return fy;
  let [, start, end] = match;
  if (start.length === 4) start = start.slice(2);
  if (end.length === 4) end = end.slice(2);
  return `FY ${start}-${end}`;
};

// Derive short FY label from a unix timestamp (ms)
const timestampToFY = (timestamp) => {
  if (!timestamp) return null;
  const d = new Date(Number(timestamp));
  if (isNaN(d)) return null;
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1;
  const endYear = startYear + 1;
  return `FY ${String(startYear).slice(2)}-${String(endYear).slice(2)}`;
};

// Current FY in short format e.g. "FY 26-27"
const getCurrentFY = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1;
  const endYear = startYear + 1;
  return `FY ${String(startYear).slice(2)}-${String(endYear).slice(2)}`;
};

const getFYRange = (fy) => {
  if (fy === "ALL") return null;
  const cleaned = fy.replace("FY ", "");
  let [startYear, endYear] = cleaned.split("-");
  if (startYear.length === 2) startYear = "20" + startYear;
  if (endYear.length === 2) endYear = "20" + endYear;
  return {
    startDate: new Date(`${startYear}-04-01`).getTime(),
    endDate: new Date(`${endYear}-03-31`).getTime(),
  };
};

// ─── Reusable UI pieces ───────────────────────────────────────────────────────
const SectionCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      p: { xs: 2, md: 2.5 },
      borderRadius: "12px",
      border: "1px solid #EBEBEB",
      background: "#FFFFFF",
      mb: 2,
      ...sx,
    }}
  >
    {children}
  </Card>
);

const SectionHeader = ({ color, title, subtitle }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1.2} mt={0.5}>
    <Box sx={{ width: 4, height: 22, borderRadius: 2, background: color, flexShrink: 0 }} />
    <Box>
      <Typography fontWeight={700} fontSize={{ xs: 16, md: 18 }} color="#1A1A2E" lineHeight={1.3}>
        {title}
      </Typography>
      <Typography fontSize={{ xs: 12, md: 13 }} color="#888">
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const SlabChip = ({ label, active, color }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.2,
      py: 0.35,
      borderRadius: 20,
      fontSize: { xs: 10, md: 11 },
      fontWeight: active ? 700 : 500,
      border: `1.5px solid ${active ? color : "#DCDCDC"}`,
      background: active ? `${color}1A` : "#F5F5F5",
      color: active ? color : "#888",
      whiteSpace: "nowrap",
      cursor: "default",
    }}
  >
    {active && (
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
    )}
    {label}
  </Box>
);

const VSep = () => (
  <Box sx={{ width: "1px", background: "#F0F0F0", alignSelf: "stretch" }} />
);

const StatsRow = ({ cells }) => (
  <Box display="flex" sx={{ border: "1px solid #F0F0F0", borderRadius: 2, overflow: "hidden", mt: 2 }}>
    {cells.map((cell, i) => (
      <React.Fragment key={i}>
        {i > 0 && <VSep />}
        <Box flex={1} p={1.5} textAlign="center">
          <Typography sx={{ fontSize: 10, color: "#999", fontWeight: 600, letterSpacing: "0.4px" }}>
            {cell.label}
          </Typography>
          <Typography fontWeight={700} fontSize={{ xs: 14, md: 16 }} color={cell.color || "#1A1A2E"} mt={0.4}>
            {cell.value}
          </Typography>
        </Box>
      </React.Fragment>
    ))}
  </Box>
);

const TipBox = ({ children, type = "unlock" }) => (
  <Box
    sx={{
      mt: 1.5, p: 1.3,
      background: type === "top" ? "#F0FFF6" : "#FFFBF0",
      border: `1px solid ${type === "top" ? "#A5D6A7" : "#FFE082"}`,
      borderRadius: 2,
      display: "flex", alignItems: "center", gap: 0.8,
    }}
  >
    <Typography fontSize={14}>{type === "top" ? "🏆" : "💡"}</Typography>
    <Typography fontSize={{ xs: 12, md: 13 }} color={type === "top" ? "#2E7D32" : "#B76E00"} fontWeight={500}>
      {children}
    </Typography>
  </Box>
);

const ViewDetailsBtn = ({ color, onClick }) => (
  <Button
    fullWidth variant="text" onClick={onClick}
    sx={{
      mt: 1.5, pt: 1.3,
      fontWeight: 600, fontSize: { xs: 12, md: 13 },
      color, textTransform: "none",
      borderTop: "1px solid #F0F0F0", borderRadius: 0,
      "&:hover": { background: "transparent", opacity: 0.75 },
    }}
  >
    View Details &nbsp;→
  </Button>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═════════════════════════════════════════════════════════════════════════════
export default function CpPayoutDashBoard() {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const [showAOPDetails, setShowAOPDetails] = React.useState(false);
  const [showPOPDetails, setShowPOPDetails] = React.useState(false);
  const [showBaseBrokerageDetails, setShowBaseBrokerageDetails] = React.useState(false);
  const [selectedAOPPlan, setSelectedAOPPlan] = React.useState(null);
  const [selectedPOPPlan, setSelectedPOPPlan] = React.useState(null);
  const [selectedBasePlan, setSelectedBasePlan] = React.useState(null);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);

  const [aopPlansData, setAopPlansData] = React.useState([]);
  const [popPlansData, setPopPlansData] = React.useState([]);
  const [baseBrokeragePlansData, setBaseBrokeragePlansData] = React.useState([]);
  const [selectedFY, setSelectedFY] = React.useState(getCurrentFY());
  const [fyMenuAnchor, setFyMenuAnchor] = React.useState(null);

  // Scroll to top on every mount
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  const loggedInUser = useSelector((state) => state.Auth.user);
  const cpId = loggedInUser?.cpId;

  const fyOptions = useMemo(() => {
    const set = new Set();
    aopPlansData.forEach((p) => {
      const fy = normalizeFY(p.plan?.financial_year);
      if (fy) set.add(fy);
    });
    popPlansData.forEach((p) => {
      const fy = timestampToFY(p.plan?.start_date);
      if (fy) set.add(fy);
    });
    baseBrokeragePlansData.forEach((p) => {
      const fy = timestampToFY(p.plan?.start_date);
      if (fy) set.add(fy);
    });
    return ["ALL", ...Array.from(set).sort().reverse()];
  }, [aopPlansData, popPlansData, baseBrokeragePlansData]);

  const filteredAOPPlans = useMemo(() =>
    selectedFY === "ALL"
      ? aopPlansData
      : aopPlansData.filter((p) => normalizeFY(p.plan?.financial_year) === selectedFY),
  [selectedFY, aopPlansData]);

  const filteredPOPPlans = useMemo(() => {
    if (selectedFY === "ALL") return popPlansData;
    return popPlansData.filter((p) => timestampToFY(p.plan?.start_date) === selectedFY);
  }, [selectedFY, popPlansData]);

  const filteredBasePlans = useMemo(() => {
    if (selectedFY === "ALL") return baseBrokeragePlansData;
    return baseBrokeragePlansData.filter((p) => timestampToFY(p.plan?.start_date) === selectedFY);
  }, [selectedFY, baseBrokeragePlansData]);

  // ─── Data loading ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!cpId) return;
    const loadData = async () => {
      try {
        // 1) AOP
        const { plans: allAOPPlans } = await fetchAllAOPPlans();
        const formattedAOPPlans = await Promise.all(
          (allAOPPlans || []).map(async (plan) => {
            const { data, slabs } = await fetchAOPData(cpId, plan.id);
            console.log("Fetched AOP Data for plan", plan.id, { data, slabs });
            const result = transformAOPData(data || [], slabs || []);
            return {
              plan, slabs: slabs || [], stats: result, rows: data || [],
              currentSlab: [...(slabs || [])].reverse().find((s) => result.totalValue >= Number(s.from_value)) || null,
              nextSlab: (slabs || []).find((s) => Number(s.from_value) > result.totalValue) || null,
            };
          })
        );
        setAopPlansData(formattedAOPPlans.filter((p) => p.slabs.length > 0));

        // 2) POP
        const { plans } = await fetchPOPData(cpId);
        setPopPlansData(
          (plans || []).map((p) => {
            const result = transformPOPData(p.data || [], p.slabs || []);
            return {
              plan: p.plan, slabs: p.slabs || [], stats: result, rows: p.data || [],
              currentSlab: [...(p.slabs || [])].reverse().find((s) => result.totalUnits >= Number(s.from_value)) || null,
              nextSlab: (p.slabs || []).find((s) => Number(s.from_value) > result.totalUnits) || null,
            };
          })
        );

        // 3) BASE BROKERAGE
        const { data: basePlans, error: basePlansError } = await supabase
          .from("base_brokerage_plans").select("*").is("deleted_at", null)
          .eq("status", "Active").order("start_date", { ascending: false });
        if (basePlansError) throw basePlansError;

        const { data: allBasePayments, error: basePaymentError } = await supabase
          .from("CpPayments")
          .select("*, AllotmentPayment (allotmentPaymentId, unitId, inventoryMergedId, created_at)")
          .eq("CpId", cpId).order("created_at", { ascending: false })
          .not("AllotmentPayment", "is", null);
        if (basePaymentError) throw basePaymentError;

        const unitIds = (allBasePayments || []).map((r) => r?.AllotmentPayment?.unitId).filter(Boolean);
        let inventoryMap = {};
        if (unitIds.length > 0) {
          const { data: inventoryRows, error: inventoryError } = await supabase
            .from("Inventory")
            .select("unitId, projectId, wingId, Projects (projectId, projectName), Wings (wingId, wingName)")
            .in("unitId", unitIds);
          if (inventoryError) throw inventoryError;
          inventoryMap = (inventoryRows || []).reduce((acc, inv) => { acc[inv.unitId] = inv; return acc; }, {});
        }

        const { data: fvPayments, error: fvError } = await supabase
  .from("CPFVPaymentsHistory")
  .select(`Amount, CPpaymentId`)
  .in(
    "CPpaymentId",
    (allBasePayments || []).map((p) => p.id).filter(Boolean)
  );

if (fvError) throw fvError;
const totalPaidAmount = (fvPayments || []).reduce(
  (sum, row) => sum + Number(row.Amount || 0),
  0
);
setTotalPaidAmount(totalPaidAmount);

        setBaseBrokeragePlansData(
          (basePlans || []).map((plan) => {
            const startDate = Number(plan.start_date || 0);
            const endDate = plan?.end_date ? Number(plan.end_date) : new Date().getTime();
            const matchedRows = (allBasePayments || [])
              .map((row) => ({ ...row, rowTime: row?.AllotmentPayment?.created_at ? Number(row.AllotmentPayment.created_at) : 0 }))
              .filter((row) => row.rowTime >= startDate && row.rowTime <= endDate);
            const rowsWithComputed = matchedRows.map((row) => {
              const unitId = row?.AllotmentPayment?.unitId;
              const inventory = inventoryMap[unitId];
              const bookingValue = Number(row.Agreement_Value || 0);
              const brokerageAmount =
                Number(row.Brokerage_Amount_Basic || 0) + Number(row.Brokerage_Amount_Tax_CGST || 0) +
                Number(row.Brokerage_Amount_Tax_SGST || 0) + Number(row.Brokerage_Amount_Tax_IGST || 0);
                const status = row?.Status || row?.status || "-";
              return {
                ...row, booking_value: bookingValue, base_brokerage_amount: brokerageAmount,
                projectName: inventory?.Projects?.projectName || "-",
                wingName: inventory?.Wings?.wingName || "-",
                created_at: row?.AllotmentPayment?.created_at || row?.created_at || null,
                   status,
              };
            });
            const totalUnits = rowsWithComputed.length;
            const totalValue = rowsWithComputed.reduce((s, r) => s + Number(r.booking_value || 0), 0);
            const totalBrokerage = rowsWithComputed.reduce((s, r) => s + Number(r.base_brokerage_amount || 0), 0);
            const target =
              plan.calc_type === "Percentage" ? (totalValue > 0 ? totalValue * 2 : 1000000) : (totalUnits > 0 ? totalUnits * 2 : 10);
            const currentProgress = plan.calc_type === "Percentage" ? totalValue : totalUnits;
            const achievement = target ? Math.min((currentProgress / target) * 100, 100) : 0;
            return {
              plan, rows: rowsWithComputed,
              stats: { totalUnits, totalValue, totalBrokerage, target, achievement },
              slabs: [{ id: plan.id, label: getBaseBrokerageLabel(plan) }],
              currentSlab: { id: plan.id, label: getBaseBrokerageLabel(plan) },
            };
          })
        );
      } catch (err) {
        console.error("❌ Dashboard Load Error:", err);
        setAopPlansData([]); setPopPlansData([]); setBaseBrokeragePlansData([]);
      }
    };
    loadData();
  }, [cpId]);

  useEffect(() => { dispatch(action.activeMenu("payoutdashboard")); }, []);

  // Detail pages
  if (showAOPDetails && selectedAOPPlan)
    return <AOPDetailsPage onBack={() => setShowAOPDetails(false)} selectedPlan={selectedAOPPlan} cpId={cpId} />;
  if (showPOPDetails && selectedPOPPlan)
    return <POPDetailsPage onBack={() => setShowPOPDetails(false)} selectedPlan={selectedPOPPlan} cpId={cpId} />;
  if (showBaseBrokerageDetails && selectedBasePlan)
    return <BaseBrokerageDetailsPage onBack={() => setShowBaseBrokerageDetails(false)} selectedPlan={selectedBasePlan} cpId={cpId} aopPlansData={aopPlansData} />;

  // ─── Totals ──────────────────────────────────────────────────────────────
  const totalPOPReward     = filteredPOPPlans.reduce((s, p) => s + (p.stats?.totalReward || 0), 0);
  const totalAOPIncentive  = filteredAOPPlans.reduce((s, p) => s + (p.stats?.totalIncentive || 0), 0);
  const totalBaseBrokerage = filteredBasePlans.reduce((s, p) => s + (p.stats?.totalBrokerage || 0), 0);
  const grandTotal = totalBaseBrokerage + totalAOPIncentive + totalPOPReward;
const totalEarned = totalBaseBrokerage + totalAOPIncentive + totalPOPReward;

const receivedValue = totalPaidAmount;
const pendingValue = Math.max(totalEarned - totalPaidAmount, 0);

const receivedAmt = formatCurrency(receivedValue);
const pendingAmt = formatCurrency(pendingValue);

const receivedPct = totalEarned > 0 ? (receivedValue / totalEarned) * 100 : 0;
const pendingPct = totalEarned > 0 ? (pendingValue / totalEarned) * 100 : 0;


  return (
    <Box
      ref={scrollRef}
      sx={{
        p: { xs: 2, md: 3 },
        background: "#F5F6FA",
        height: { xs: "75vh", md: "85vh" },
        overflowY: "auto",
        overflowX: "hidden",
        pb: { xs: `calc(4rem + env(safe-area-inset-bottom))`, md: 3 },
      }}
    >
      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
        <Box>
          <Typography fontSize={{ xs: 20, md: 24 }} fontWeight={700} color="#1A1A2E" lineHeight={1.2}>
            Earnings &amp; Performance Hub
          </Typography>
          <Typography fontSize={{ xs: 13, md: 14 }} color="#888" mt={0.3}>
            Track your growth. Unlock higher rewards.
          </Typography>
        </Box>
        <Box>
          <Box
            onClick={(e) => setFyMenuAnchor(e.currentTarget)}
            sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 2, py: 0.85,
              borderRadius: 2.5,
              background: "linear-gradient(135deg, #6C63FF 0%, #4F46E5 100%)",
              color: "#fff",
              fontSize: 13, fontWeight: 700,
              cursor: "pointer", userSelect: "none",
              boxShadow: "0 4px 14px rgba(108,99,255,0.35)",
              transition: "opacity 0.15s",
              "&:hover": { opacity: 0.88 },
            }}
          >
            <Typography fontSize={14} lineHeight={1}>📅</Typography>
            <Typography fontSize={13} fontWeight={700} lineHeight={1}>
              {selectedFY === "ALL" ? "All Years" : selectedFY}
            </Typography>
            <Typography fontSize={11} lineHeight={1} sx={{ opacity: 0.8 }}>▾</Typography>
          </Box>

          <Menu
            anchorEl={fyMenuAnchor}
            open={Boolean(fyMenuAnchor)}
            onClose={() => setFyMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 0.8, borderRadius: 2,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                minWidth: 150,
                "& .MuiList-root": { py: 0.5 },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {fyOptions.map((fy) => (
              <MenuItem
                key={fy}
                selected={fy === selectedFY}
                onClick={() => { setSelectedFY(fy); setFyMenuAnchor(null); }}
                sx={{
                  fontSize: 13,
                  fontWeight: fy === selectedFY ? 700 : 400,
                  color: fy === selectedFY ? "#4F46E5" : "#374151",
                  borderRadius: 1.5, mx: 0.5, my: 0.2,
                  "&.Mui-selected": { background: "#EEF2FF" },
                  "&:hover": { background: "#F5F3FF" },
                }}
              >
                {fy === "ALL" ? "All Years" : fy}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {/* ════════════════════════════════════════
          TOP SUMMARY CARD
      ════════════════════════════════════════ */}
      <SectionCard sx={{ mb: 2.5 }}>
        {/* Top row: label + units badge */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography fontSize={{ xs: 10, md: 11 }} color="#888" fontWeight={600} letterSpacing={0.5}>
            FY TOTAL EARNINGS (BASIC + AOP + POP)
          </Typography>
        
        </Box>

        {/* Grand total amount */}
        <Typography
          fontSize={{ xs: 30, md: 38 }} fontWeight={800} color="#1A1A2E"
          mt={0.5} mb={0.5} lineHeight={1}
        >
          ₹{formatCurrency(grandTotal)}
        </Typography>

        {/* Dual progress bar */}
        <DualProgressBar
          receivedAmt={receivedAmt}
          pendingAmt={pendingAmt}
          receivedPct={receivedPct}
          pendingPct={pendingPct}
        />

        {/* Three stat sub-cards */}
        <Box
          mt={2}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
            border: "2px solid #F0F0F0",
            borderRadius: 5,
            overflow: "hidden",
            background: "#F5F6FA",
          }}
        >
          {/* BASIC BROKERAGE */}
          <Box
            p={{ xs: 1.5, md: 2 }}
            textAlign="center"
            sx={{
              gridColumn: { xs: "1 / -1", sm: "auto" },
              borderBottom: { xs: "1px solid #F0F0F0", sm: "none" },
              borderRight: { sm: "1px solid #F0F0F0" },
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.4} mb={0.4}>
              <Typography fontSize={11}>📄</Typography>
              <Typography fontSize={{ xs: 9, md: 10 }} color="#888" fontWeight={600} letterSpacing={0.4}>
                BASIC BROKERAGE
              </Typography>
            </Box>
            <Typography fontWeight={700} fontSize={{ xs: 17, md: 20 }} color="#1A1A2E">
              ₹{formatCurrency(totalBaseBrokerage)}
            </Typography>
            <Typography
              onClick={() => {
                setSelectedBasePlan(filteredBasePlans?.[0] || null);
                setShowBaseBrokerageDetails(true);
              }}
              fontSize={{ xs: 11, md: 12 }}
              color="#3BB273"
              fontWeight={600}
              mt={0.3}
              sx={{ cursor: "pointer", textDecoration: "underline" }}
            >
              View details →
            </Typography>
          </Box>

          {/* AOP INCENTIVE */}
          <Box
            p={{ xs: 1.5, md: 2 }}
            textAlign="center"
            sx={{
              borderRight: { xs: "1px solid #F0F0F0", sm: "1px solid #F0F0F0" },
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.4} mb={0.4}>
              <Typography fontSize={11}>📈</Typography>
              <Typography fontSize={{ xs: 9, md: 10 }} color="#888" fontWeight={600} letterSpacing={0.4}>
                AOP INCENTIVE
              </Typography>
            </Box>
            <Typography fontWeight={700} fontSize={{ xs: 17, md: 20 }} color="#1A1A2E">
              ₹{formatCurrency(totalAOPIncentive)}
            </Typography>
            <Typography fontSize={{ xs: 11, md: 12 }} color="#F9A825" fontWeight={500} mt={0.3}>
              FY end payout
            </Typography>
          </Box>

          {/* POP INCENTIVE */}
          <Box
            p={{ xs: 1.5, md: 2 }}
            textAlign="center"
          >
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.4} mb={0.4}>
              <Typography fontSize={11}>🚀</Typography>
              <Typography fontSize={{ xs: 9, md: 10 }} color="#888" fontWeight={600} letterSpacing={0.4}>
                POP INCENTIVE
              </Typography>
            </Box>
            <Typography fontWeight={700} fontSize={{ xs: 17, md: 20 }} color="#1A1A2E">
              ₹{formatCurrency(totalPOPReward)}
            </Typography>
            <Typography fontSize={{ xs: 11, md: 12 }} color="#F9A825" fontWeight={500} mt={0.3}>
              FY end payout
            </Typography>
          </Box>
        </Box>
      </SectionCard>

      {/* ════════════════════════════════════════
          EMPTY STATE
      ════════════════════════════════════════ */}
      {filteredBasePlans.length === 0 && filteredAOPPlans.length === 0 && filteredPOPPlans.length === 0 && (
        <SectionCard sx={{ textAlign: "center", py: 5, mb: 2 }}>
          <Typography fontSize={48} mb={1.5}>📭</Typography>
          <Typography fontWeight={700} fontSize={{ xs: 16, md: 18 }} color="#1A1A2E" mb={0.8}>
            No Plans for {selectedFY}
          </Typography>
          <Typography fontSize={{ xs: 13, md: 14 }} color="#888" mb={2.5} px={2}>
            There are no Base Brokerage, AOP, or POP plans assigned to you for this financial year.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSelectedFY("ALL")}
            sx={{
              textTransform: "none", fontWeight: 600, fontSize: 13,
              borderColor: "#6C63FF", color: "#6C63FF",
              borderRadius: 2, px: 2.5,
              "&:hover": { background: "#F3F2FF", borderColor: "#6C63FF" },
            }}
          >
            View All Years
          </Button>
        </SectionCard>
      )}

      {/* ════════════════════════════════════════
          BASE BROKERAGE
      ════════════════════════════════════════ */}
      {filteredBasePlans.length > 0 && (
        <>
          <SectionHeader
            color="#FF9800"
            title="Base Brokerage"
            subtitle="Standard brokerage earned on bookings"
          />
          <Grid container spacing={1.5} sx={{ mb: 1 }}>
            {filteredBasePlans.map((item, i) => (
              <Grid item xs={12} sm={filteredBasePlans.length === 1 ? 12 : 6} key={i}>
                <SectionCard sx={{ mb: 0 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography fontWeight={700} fontSize={{ xs: 15, md: 17 }} color="#1A1A2E">
                        {item.plan?.name || "Base Brokerage Plan"}
                      </Typography>
                      <Typography fontSize={12} color="#888" mt={0.3}>
                        📅 {formatDate(item.plan?.start_date)} – {formatDate(item.plan?.end_date)}
                      </Typography>
                      <Typography fontSize={12} color="#888" mt={0.2}>
                        📋 {item.plan?.calc_type} • {getBaseBrokerageLabel(item.plan)}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography fontWeight={800} fontSize={{ xs: 22, md: 26 }} color="#1A1A2E" lineHeight={1}>
                        {item.stats.achievement.toFixed(0)}%
                      </Typography>
                      <Typography fontSize={12} color="#888">achieved</Typography>
                    </Box>
                  </Box>

                  <ProgressBar value={item.stats.achievement} color="#FF9800" />

                  <Typography fontSize={10} color="#999" fontWeight={600} letterSpacing={0.4} mt={1.5} mb={0.8}>
                    SLAB LEVEL
                  </Typography>
                  <Box display="flex" gap={0.8} flexWrap="wrap">
                    <SlabChip label={item.currentSlab?.label || "-"} active color="#FF9800" />
                  </Box>

                  <StatsRow cells={[
                    { label: "UNITS", value: item.stats.totalUnits },
                    { label: "SOLD VALUE", value: `₹${formatCurrency(item.stats.totalValue)}` },
                    { label: "BROKERAGE", value: `₹${formatCurrency(item.stats.totalBrokerage)}`, color: "#FF9800" },
                  ]} />

                  <ViewDetailsBtn color="#FF9800" onClick={() => { setSelectedBasePlan(item); setShowBaseBrokerageDetails(true); }} />
                </SectionCard>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* ════════════════════════════════════════
          AOP
      ════════════════════════════════════════ */}
      {filteredAOPPlans.length > 0 && (
      <>
      <SectionHeader
        color="#6C63FF"
        title="AOP — Annual Operating Plan"
        subtitle="Incentive earned here is payable at Financial Year End"
      />
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        {filteredAOPPlans.map((item, i) => (
          <Grid item xs={12} key={i}>
            <SectionCard>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography fontWeight={700} fontSize={{ xs: 15, md: 17 }} color="#1A1A2E">
                    {item.plan?.financial_year || "AOP Plan"}
                  </Typography>
                  <Typography fontSize={12} color="#888" mt={0.3}>
                    📅 {formatDate(item.plan?.validity_start_date)} – {formatDate(item.plan?.validity_end_date)}
                  </Typography>
                  <Typography fontSize={12} color="#888" mt={0.2}>
                    📋 Agreement done leads only
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography fontWeight={800} fontSize={{ xs: 22, md: 26 }} color="#1A1A2E" lineHeight={1}>
                    {item.stats.achievement.toFixed(0)}%
                  </Typography>
                  <Typography fontSize={12} color="#888">achieved</Typography>
                </Box>
              </Box>

              <ProgressBar value={item.stats.achievement} color="#6C63FF" />

              <Typography fontSize={10} color="#999" fontWeight={600} letterSpacing={0.4} mt={1.5} mb={0.8}>
                SLAB LEVELS
              </Typography>
              <Box display="flex" gap={0.8} flexWrap="wrap">
                {item.slabs.map((slab) => (
                  <SlabChip
                    key={slab.id}
                    label={getSlabLabel(slab)}
                    active={item.currentSlab?.id === slab.id}
                    color="#6C63FF"
                  />
                ))}
              </Box>

              <StatsRow cells={[
                { label: "SOLD", value: `₹${formatCurrency(item.stats.totalValue)}` },
                { label: "CURRENT SLAB", value: item.currentSlab ? getSlabLabel(item.currentSlab) : "-", color: "#6C63FF" },
                { label: "INCENTIVE", value: `₹${formatCurrency(item.stats.totalIncentive)}`, color: "#F9A825" },
              ]} />

              {item.nextSlab && (
                <TipBox>
                  Sell ₹{formatCurrency(Number(item.nextSlab.from_value) - item.stats.totalValue)} more — unlock {getSlabLabel(item.nextSlab)} slab
                </TipBox>
              )}

              <ViewDetailsBtn color="#6C63FF" onClick={() => { setSelectedAOPPlan(item); setShowAOPDetails(true); }} />
            </SectionCard>
          </Grid>
        ))}
      </Grid>
      </>
      )}

      {/* ════════════════════════════════════════
          POP
      ════════════════════════════════════════ */}
      {filteredPOPPlans.length > 0 && (
      <>
      <SectionHeader
        color="#3BB273"
        title="POP — Project Performance"
        subtitle="Tactical project-level brokerage tracking"
      />
      <Grid container spacing={1.5}>
        {filteredPOPPlans.map((item, i) => (
          <Grid item xs={12} sm={popPlansData.length === 1 ? 12 : 6} key={i}>
            <SectionCard sx={{ mb: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography fontWeight={700} fontSize={{ xs: 15, md: 17 }} color="#1A1A2E">
                    {item.plan?.name || "POP Plan"}
                  </Typography>
                  <Typography fontSize={12} color="#888" mt={0.3}>
                    📅 {formatDate(item.plan?.start_date)} – {formatDate(item.plan?.end_date)}
                  </Typography>
                  <Typography fontSize={12} color="#888" mt={0.2}>
                    📋 Agreement done leads only
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography fontWeight={800} fontSize={{ xs: 22, md: 26 }} color="#1A1A2E" lineHeight={1}>
                    {item.stats.achievement?.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>

              <ProgressBar value={item.stats.achievement || 0} color="#3BB273" />

              <Typography fontSize={10} color="#999" fontWeight={600} letterSpacing={0.4} mt={1.5} mb={0.8}>
                SLAB LEVELS
              </Typography>
              <Box display="flex" gap={0.8} flexWrap="wrap">
                {item.slabs.map((slab) => (
                  <SlabChip
                    key={slab.id}
                    label={getSlabLabel(slab)}
                    active={item.currentSlab?.id === slab.id}
                    color="#3BB273"
                  />
                ))}
              </Box>

              <StatsRow cells={[
                { label: "UNITS SOLD", value: item.stats.totalUnits },
                { label: "CURRENT SLAB", value: item.currentSlab ? getSlabLabel(item.currentSlab) : "-", color: "#3BB273" },
                { label: "REWARD", value: `₹${formatCurrency(item.stats.totalReward)}` },
              ]} />

              {item.nextSlab ? (
                <TipBox>
                  Close {item.nextSlab.from_value - item.stats.totalUnits} more deal — unlock {getSlabLabel(item.nextSlab)} reward
                </TipBox>
              ) : item.currentSlab ? (
                <TipBox type="top">Top slab unlocked!</TipBox>
              ) : null}

              <ViewDetailsBtn color="#3BB273" onClick={() => { setSelectedPOPPlan(item); setShowPOPDetails(true); }} />
            </SectionCard>
          </Grid>
        ))}
      </Grid>
      </>
      )}
    </Box>
  );
}